import { Card, Col, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Polygon, StyledMap } from 'components/Map';
import { MapLegend, MapLegendRow, MapLegendSwatch, MapPane } from 'components/Map/MapLayout.style';
import { isTest } from 'utils/env';
import { getBounds } from 'utils/geo';

import { PointsLayer, PointsList, TurbinesList } from './';

export const TurbinePointMap = ({
  items,
  enableClustering,
  getIcon,
  icon,
  iconProps,
  loading,
  height = '35rem',
  spiderfy = false,
  satelliteView = false,
  mapHeight,
  sideList = false,
  setTurbineToUpdate,
  isDraggable,
  isWorkOrderTasksList = false,
  isActive,
  siteBoundaries = /** @type {import('utils/latLng').Boundary[]} */ ([]),
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMapActive = isActive ?? true;
  const shouldPreserveView = isActive !== undefined;
  const [selectedTurbine, setSelectedTurbine] = useState();
  /*
    Using a ref to access the map instance is no longer possible
    Saving a copy of the map instance to state instead
    https://github.com/PaulLeCam/react-leaflet/issues/841
  */
  const [mapRef, setMapRef] = useState();
  const [mapRefReady, setMapRefReady] = useState(false);
  const [isInitial, setIsInitial] = useState(false);
  const [hasDraggingStarted, setHasDraggingStarted] = useState(false);

  const getPosition = x => x.position;

  const getMarkerBounds = layers => {
    if (!layers.length) {
      return undefined;
    }

    const bounds = getBounds(layers.map(getPosition)).pad(0.1);

    const { _northEast, _southWest } = bounds;

    return [
      [_northEast.lat, _northEast.lng],
      [_southWest.lat, _southWest.lng],
    ];
  };

  const MAX_ZOOM_LEVEL = 18;
  const SELECTED_TURBINE_INITIAL = 0;

  function handleItemClick(item, index) {
    const { lat, lng } = item?.position;
    setSelectedTurbine(index);
    // use the map reference to call the flyTo method to send the
    // user to the item they clicked on in the left nav.
    mapRef.flyTo({ lat, lng }, MAX_ZOOM_LEVEL);
  }

  // returns an array of array pairs. used by react-leaflet to set the bounds of the map
  // example return looks like [[80, 80],[90,90]]
  function getSelectedItemBounds(selectedTurbineIndex) {
    if (selectedTurbine >= 0) {
      const selectedItemArray = items.filter((_i, index) => {
        return index === selectedTurbineIndex;
      });

      return getMarkerBounds(selectedItemArray);
    }
  }

  const listColSpan = sideList ? 4 : 0;
  const turbinesColSpan = sideList || isWorkOrderTasksList ? 0 : 6;
  const mapColSpan = 24 - listColSpan - turbinesColSpan;
  const isInitialRenderInPreview = sideList && mapRefReady && !hasDraggingStarted;
  const readyToFitInitialBounds =
    sideList && selectedTurbine === SELECTED_TURBINE_INITIAL && isInitial;

  /*  
      this should only ever be called once, to set the initially selected turbine
      and then to mark that this is the initial logic for setup. only for create
      asset preview map.
   */
  useEffect(() => {
    if (isInitialRenderInPreview) {
      setSelectedTurbine(SELECTED_TURBINE_INITIAL);
      setIsInitial(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, mapRefReady, sideList, items, hasDraggingStarted]);

  // this will be invoked if the initial useEffect has happened, we set the maps bounds
  // to the bounds of the first item in the array. only for create asset preview map.
  useEffect(() => {
    if (readyToFitInitialBounds) {
      const initialBounds = getSelectedItemBounds(SELECTED_TURBINE_INITIAL);
      mapRef.fitBounds(initialBounds);
      setIsInitial(false);
    }
  }, [isInitial, mapRef, sideList, selectedTurbine]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only the Asset Details keep-alive map needs a size refresh when shown again.
    if (!shouldPreserveView || !isMapActive || !mapRef || !mapRefReady) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      mapRef.invalidateSize();
    });
    return () => cancelAnimationFrame(frameId);
  }, [shouldPreserveView, isMapActive, mapRef, mapRefReady]);

  /*
    Passing isActive opts the Asset Details keep-alive map into stable bounds. This preserves the
    user's view across tab changes without changing the existing re-fit behavior of other
    TurbinePointMap consumers. State is adjusted during render (React's supported pattern for
    deriving cache from props) so StyledMap still receives the updated bounds in the same pass.
  */
  const nextBounds =
    // If selectedTurbine is set, fit bounds to map center to zoom to selected item
    selectedTurbine >= 0
      ? getSelectedItemBounds(selectedTurbine)
      : siteBoundaries.length
        ? getBounds([
            ...siteBoundaries.flat(),
            ...items.map(item => [item.position.lat, item.position.lng]),
          ]).pad(0.1)
        : getMarkerBounds(items);
  const nextBoundsKey = shouldPreserveView ? JSON.stringify(nextBounds) : undefined;
  const [preservedBounds, setPreservedBounds] = useState({
    key: undefined,
    bounds: undefined,
  });

  if (shouldPreserveView && isMapActive && nextBoundsKey !== preservedBounds.key) {
    setPreservedBounds({ key: nextBoundsKey, bounds: nextBounds });
  }

  const bounds = shouldPreserveView ? preservedBounds.bounds : nextBounds;

  return (
    <Card
      styles={{ body: { height, padding: 0 } }}
      loading={loading && siteBoundaries.length === 0}
      style={{ width: '100%' }}
    >
      <Row>
        <Col span={listColSpan}>
          {sideList && (
            <PointsList
              mapHeight={mapHeight}
              data={items}
              onItemClick={handleItemClick}
              selectedTurbineIndex={selectedTurbine}
            ></PointsList>
          )}
        </Col>
        <Col span={turbinesColSpan}>
          {!sideList && !isWorkOrderTasksList && (
            <TurbinesList containerHeight={height} data={items} />
          )}
        </Col>
        <Col span={mapColSpan}>
          <MapPane>
            <StyledMap
              mapRef={mapRef}
              setMapRef={setMapRef}
              mapRefReady={mapRefReady}
              setMapRefReady={setMapRefReady}
              bounds={bounds}
              /*
               * -- FIXME ---
               * Currently we need to disable clustering during testing. Updating react-leaflet and plugins
               * should resolve this.
               * https://github.com/yuzhva/react-leaflet-markercluster/issues/85
               */
              enableClustering={isTest ? undefined : enableClustering}
              spiderfy={spiderfy}
              satelliteView={satelliteView}
              height={height}
              sideList={sideList}
              layersControl={true}
              mapOverlays={
                mapRefReady &&
                siteBoundaries.map((coords, index) => (
                  <Polygon
                    key={`site-boundary-${index}`}
                    positions={coords}
                    interactive={false}
                    pathOptions={{ color: theme.yellow, weight: 3, fillOpacity: 0 }}
                  />
                ))
              }
            >
              <PointsLayer
                mapRef={mapRef}
                items={items}
                getIcon={getIcon}
                icon={icon}
                iconProps={iconProps}
                selectedTurbineIndex={selectedTurbine}
                setSelectedTurbine={setSelectedTurbine}
                setTurbineToUpdate={setTurbineToUpdate}
                isDraggable={isDraggable}
                setHasDraggingStarted={setHasDraggingStarted}
                isWorkOrderTasksList={isWorkOrderTasksList}
              />
              <PointsLayer
                mapRef={mapRef}
                items={items}
                getIcon={getIcon}
                icon={icon}
                iconProps={iconProps}
                selectedTurbineIndex={selectedTurbine}
                setSelectedTurbine={setSelectedTurbine}
                setTurbineToUpdate={setTurbineToUpdate}
                isDraggable={isDraggable}
                setHasDraggingStarted={setHasDraggingStarted}
                isLabelLayer={true}
                isWorkOrderTasksList={isWorkOrderTasksList}
              />
            </StyledMap>
            {siteBoundaries.length > 0 && (
              <MapLegend>
                <MapLegendRow>
                  <MapLegendSwatch $color={theme.yellow} />
                  {t('assets.siteBoundary', 'Site Boundary')}
                </MapLegendRow>
              </MapLegend>
            )}
          </MapPane>
        </Col>
      </Row>
    </Card>
  );
};
